const { getCountries, getCountryCallingCode, parsePhoneNumberFromString } = require('./phoneLibrary');

// Kept compatible with locationScopes: country arrays retain their indexed blanks.
function parseLocationCellValue(rawValue) {
  if (Array.isArray(rawValue)) return rawValue.map((value) => String(value ?? '').trim());
  const trimmedValue = String(rawValue ?? '').trim();
  if (!trimmedValue) return [];
  if (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) {
    try {
      const parsed = JSON.parse(trimmedValue);
      if (Array.isArray(parsed)) return parsed.map((value) => String(value ?? '').trim());
    } catch {
      // Legacy single-value support.
    }
  }
  return [trimmedValue];
}

function normalizeText(value) {
  return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .trim().toLowerCase().replace(/\s+/g, ' ');
}

function normalizeCountryLabel(value) {
  return normalizeText(value).replace(/[^a-z0-9]/g, '');
}

let countryCodesByLabel;
const callingCodesByCountry = new Map();
const sharedCallingCodesByCountries = new Map();
let internationalCallingCodes;

function getCachedCallingCode(country) {
  if (!callingCodesByCountry.has(country)) callingCodesByCountry.set(country, getCountryCallingCode(country));
  return callingCodesByCountry.get(country);
}

function getInternationalCallingCode(digits) {
  // These prefixes cover common edits without initializing any country data.
  if (digits.startsWith('1')) return '1';
  if (digits.startsWith('7')) return '7';
  if (!internationalCallingCodes) {
    internationalCallingCodes = new Set(getCountries().map((country) => getCachedCallingCode(country)));
  }
  for (let length = 1; length <= 3 && length <= digits.length; length += 1) {
    const prefix = digits.slice(0, length);
    if (internationalCallingCodes.has(prefix)) return prefix;
  }
  return '';
}

function getCountryCodesByLabel() {
  if (countryCodesByLabel) return countryCodesByLabel;

  const countries = getCountries();
  const labels = new Map(countries.map((country) => [normalizeCountryLabel(country), country]));
  if (typeof Intl.DisplayNames === 'function') {
    ['es', 'en'].forEach((locale) => {
      ['long', 'short'].forEach((style) => {
        const displayNames = new Intl.DisplayNames([locale], { type: 'region', style });
        countries.forEach((country) => {
          labels.set(normalizeCountryLabel(displayNames.of(country)), country);
        });
      });
    });
  }
  Object.entries({
    Colombia: 'CO',
    'EE.UU.': 'US',
    EEUU: 'US',
    USA: 'US',
    'U.S.': 'US',
    'Estados Unidos': 'US',
    'Estados Unidos de América': 'US',
    UK: 'GB',
    'Reino Unido': 'GB',
    'Gran Bretaña': 'GB',
    Inglaterra: 'GB',
    England: 'GB',
  }).forEach(([label, country]) => labels.set(normalizeCountryLabel(label), country));

  countryCodesByLabel = labels;
  return labels;
}

function getSharedCallingCode(countryValue) {
  const labels = parseLocationCellValue(countryValue).filter(Boolean);
  if (labels.length === 0) return '';

  const key = labels.map((label) => normalizeCountryLabel(label)).join('|');
  if (sharedCallingCodesByCountries.has(key)) return sharedCallingCodesByCountries.get(key);

  const countryLabels = getCountryCodesByLabel();
  let sharedCallingCode = '';
  for (const label of labels) {
    const country = countryLabels.get(normalizeCountryLabel(label));
    if (!country) {
      sharedCallingCodesByCountries.set(key, '');
      return '';
    }
    const callingCode = getCachedCallingCode(country);
    if (sharedCallingCode && sharedCallingCode !== callingCode) {
      sharedCallingCodesByCountries.set(key, '');
      return '';
    }
    sharedCallingCode = callingCode;
  }
  if (sharedCallingCodesByCountries.size >= 1000) sharedCallingCodesByCountries.clear();
  sharedCallingCodesByCountries.set(key, sharedCallingCode);
  return sharedCallingCode;
}

function isPhoneColumnLabel(header) {
  return /^(?:(?:solicitado por|solicitante|usuario)\s*-\s*)?(?:telefono|numero de telefono|phone(?: number)?|telephone(?: number)?)(?:\s+(?:actual|solicitad[oa]|nuev[oa]|current|requested|new))?$/.test(normalizeText(header));
}

function formatTenDigits(digits) {
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function parsePossibleNumber(value, callingCode = '') {
  // Keep this shared CommonJS module free of object spread: CRA's production
  // Babel transform otherwise injects ESM helpers alongside module.exports.
  const options = { extract: false };
  if (callingCode) options.defaultCallingCode = callingCode;
  const number = parsePhoneNumberFromString(value, options);
  return number?.isPossible() ? number : null;
}

function formatInternationalNumber(number) {
  return number.nationalNumber.length === 10
    ? `+${number.countryCallingCode} ${formatTenDigits(number.nationalNumber)}`
    : number.formatInternational();
}

function formatPhoneNumber(value, countryValue = '', fallback = '') {
  const originalValue = String(value ?? '');
  const trimmedValue = originalValue.replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '').trim();
  if (!trimmedValue) return fallback;

  const extensionMatch = trimmedValue.match(/\s*(?:ext(?:ension|ensi[oó]n)?\.?|x|#|;ext=)\s*(\d+)\s*$/i);
  const extension = extensionMatch ? ` ext. ${extensionMatch[1]}` : '';
  const core = extensionMatch ? trimmedValue.slice(0, extensionMatch.index).trim() : trimmedValue;
  if (!/^\+?[\d\s()._-]+$/.test(core)) return originalValue;

  // Legacy cells can have stray or missing parentheses. Check the digits, not
  // their old punctuation, before applying the display format.
  const digits = core.replace(/\D/g, '');
  let number;
  if (core.startsWith('+')) {
    number = parsePossibleNumber(`+${digits}`);
  } else if (digits.startsWith('00')) {
    number = parsePossibleNumber(`+${digits.slice(2)}`);
  } else {
    // 011 is an international access prefix, but can also begin a national number.
    if (digits.startsWith('011')) number = parsePossibleNumber(`+${digits.slice(3)}`);
    const callingCode = getSharedCallingCode(countryValue);
    if (!number) {
      const nationalNumber = callingCode ? parsePossibleNumber(digits, callingCode) : null;
      if (nationalNumber) {
        // A location can guide grouping, but it does not prove the phone's country.
        // Only show a calling code if those digits were included in the stored value.
        if (digits === nationalNumber.nationalNumber) {
          const localDisplay = nationalNumber.nationalNumber.length === 10
            ? formatTenDigits(digits)
            : nationalNumber.formatInternational().replace(/^\+\d+\s*/, '');
          return `${localDisplay}${extension}`;
        }
        const nationalDisplay = nationalNumber.formatNational();
        if (nationalDisplay.replace(/\D/g, '') === digits) {
          return `${nationalDisplay}${extension}`;
        }
        if (digits === `${nationalNumber.countryCallingCode}${nationalNumber.nationalNumber}`) {
          return `${formatInternationalNumber(nationalNumber)}${extension}`;
        }
        return originalValue;
      }
      // Longer legacy values may already include their calling code without '+'.
      number = digits.length > 10 ? parsePossibleNumber(`+${digits}`) : null;
    }
    if (!number && digits.length === 10) {
      return `${formatTenDigits(digits)}${extension}`;
    }
  }

  if (!number) return originalValue;
  return `${formatInternationalNumber(number)}${extension}`;
}

function formatPartialNationalNumber(digits) {
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatPhoneInput(value, countryValue = '', fallback = '') {
  const originalValue = String(value ?? '');
  const trimmedValue = originalValue.replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '').trim();
  if (!trimmedValue) return fallback;
  if (/[\r\n]/.test(trimmedValue)) return originalValue;

  const extensionMatch = trimmedValue.match(/\s*(ext(?:ension|ensi[oó]n)?\.?|x|#|;ext=)\s*(\d*)\s*$/i);
  const core = extensionMatch ? trimmedValue.slice(0, extensionMatch.index).trim() : trimmedValue;
  if (!/^\+?[\d\s()._-]*$/.test(core)) return originalValue;
  const digits = core.replace(/\D/g, '');
  if (digits.length > 15) return originalValue;
  if (!digits) return core.startsWith('+') ? '+' : (extensionMatch ? originalValue : fallback);

  let internationalDigits = '';
  let explicitInternational = false;
  if (core.startsWith('+')) {
    internationalDigits = digits;
    explicitInternational = true;
  } else if (digits.startsWith('00') && digits.length > 2) {
    internationalDigits = digits.slice(2);
    explicitInternational = true;
  } else if (digits.startsWith('011') && digits.length > 3) {
    internationalDigits = digits.slice(3);
    explicitInternational = true;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    internationalDigits = digits;
    explicitInternational = true;
  }

  let formatted;
  if (explicitInternational) {
    const callingCode = getInternationalCallingCode(internationalDigits);
    if (!callingCode) {
      if (internationalDigits.length >= 3) return originalValue;
      formatted = `+${internationalDigits}`;
    } else {
      const nationalNumber = formatPartialNationalNumber(internationalDigits.slice(callingCode.length));
      formatted = `+${callingCode}${nationalNumber ? ` ${nationalNumber}` : ''}`;
    }
  } else {
    // A national number's length never supplies an untyped international code.
    formatted = formatPartialNationalNumber(digits);
  }

  if (!extensionMatch) return formatted;
  const extensionDigits = extensionMatch[2];
  return `${formatted} ${extensionDigits ? `ext. ${extensionDigits}` : extensionMatch[1]}`;
}

function formatPhoneInputForRow(header, value, headers, row, fallback = '') {
  if (!isPhoneColumnLabel(header)) return String(value ?? '').trim() || fallback;
  // Partial typing only groups supplied digits, so no country-label lookup or
  // full phone validation is necessary while the user is editing.
  return formatPhoneInput(value, '', fallback);
}

function getRowCell(headers, row, label) {
  const index = (headers || []).findIndex((header) => normalizeText(header) === normalizeText(label));
  return index >= 0 ? row?.[index] : '';
}

function resolveGlobalCountries(countryValue, stateValue) {
  const states = parseLocationCellValue(stateValue);
  return parseLocationCellValue(countryValue).map((country, index) => (
    normalizeCountryLabel(country) === 'global' ? (states[index] || 'Global') : country
  ));
}

function getRowCountries(headers, row, preferActual = false) {
  const suffixes = preferActual ? [' actual', ''] : ['', ' actual'];
  for (const suffix of suffixes) {
    const countries = getRowCell(headers, row, `Pais${suffix}`);
    if (String(countries ?? '').trim()) {
      return resolveGlobalCountries(countries, getRowCell(headers, row, `Iglesia - Estado${suffix}`));
    }
  }
  return [];
}

function getScopeCountries(value) {
  return parseLocationCellValue(value).flatMap((scope) => scope.split(/\r?\n/)).filter((line) => line.trim())
    .map((line) => {
      const parts = line.replace(/^\s*\d+[.)]\s*/, '').split('·').map((part) => part.trim());
      return normalizeCountryLabel(parts[0]) === 'global' ? (parts[1] || 'Global') : parts[0];
    });
}

function formatPhoneValueForRow(header, value, headers, row, fallback = '') {
  if (!isPhoneColumnLabel(header)) return String(value ?? '').trim() || fallback;

  const normalizedHeader = normalizeText(header);
  let countries;
  if (/^(?:solicitado por|solicitante)\s*-/.test(normalizedHeader)) {
    const prefix = normalizedHeader.startsWith('solicitante') ? 'Solicitante' : 'Solicitado por';
    countries = getScopeCountries(getRowCell(headers, row, `${prefix} - Sedes`));
  } else if (/^usuario\s*-/.test(normalizedHeader)) {
    const scopes = getRowCell(headers, row, 'Usuario - Sedes');
    countries = String(scopes ?? '').trim()
      ? getScopeCountries(scopes)
      : getRowCountries(headers, row, true);
  } else {
    countries = getRowCountries(headers, row, normalizedHeader.endsWith(' actual'));
  }
  return formatPhoneNumber(value, countries, fallback);
}

module.exports = { formatPhoneNumber, formatPhoneValueForRow, formatPhoneInput, formatPhoneInputForRow, isPhoneColumnLabel };
